import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const YEAR = new Date().getFullYear();

export function LegalLinks({ className = "" }) {
  return (
    <div className={`flex flex-wrap gap-x-5 gap-y-2 ${className}`} data-testid="legal-links">
      <Link to="/mentions-legales" className="hover:text-stone-900" data-testid="footer-mentions">Mentions légales</Link>
      <Link to="/cgv" className="hover:text-stone-900" data-testid="footer-cgv">CGV / Conditions générales</Link>
      <Link to="/confidentialite" className="hover:text-stone-900" data-testid="footer-confidentialite">Politique de confidentialité</Link>
    </div>
  );
}

function Shell({ title, children }) {
  return (
    <div className="min-h-screen bg-[#FBF9F5] flex flex-col">
      <header className="border-b bg-white">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="font-serif text-lg text-stone-900" data-testid="legal-home">Notre&nbsp;Oui</Link>
          <Link to="/login" className="text-sm text-stone-500 hover:text-stone-900">Connexion</Link>
        </div>
      </header>
      <main className="flex-1 w-full max-w-3xl mx-auto px-5 py-8 sm:py-12" data-testid="legal-page">
        <h1 className="font-serif text-3xl sm:text-4xl text-stone-900 mb-6">{title}</h1>
        <div className="prose prose-stone max-w-none text-[15px] leading-relaxed text-stone-700 space-y-4">
          {children}
        </div>
      </main>
      <footer className="border-t bg-white">
        <div className="max-w-3xl mx-auto px-5 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm text-stone-500">
          <div>© {YEAR} Notre Oui · notreoui.ch</div>
          <LegalLinks />
        </div>
      </footer>
    </div>
  );
}

function useLegal() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings/public").then(({ data }) => setS(data)).catch(() => setS({})); }, []);
  return s || {};
}

function Row({ label, value }) {
  if (!value) return null;
  return <div><span className="font-medium text-stone-900">{label} : </span>{value}</div>;
}

export function MentionsLegales() {
  const s = useLegal();
  const email = s.legal_email || "info@notreoui.ch";
  const addr = [s.legal_address, [s.legal_npa, s.legal_city].filter(Boolean).join(" "), s.legal_country].filter(Boolean);
  const hasIdentity = s.legal_name || s.legal_address;
  return (
    <Shell title="Mentions légales">
      <p>Le présent site <strong>Notre Oui</strong> (notreoui.ch) est édité par :</p>
      <div className="rounded-xl border bg-white p-5 not-prose space-y-1.5" data-testid="legal-identity">
        <Row label="Dénomination / Raison sociale" value={s.legal_name} />
        <Row label="Forme juridique" value={s.legal_form} />
        {addr.length > 0 && (
          <div><span className="font-medium text-stone-900">Adresse : </span>{addr.join(", ")}</div>
        )}
        <Row label="Email de contact" value={<a className="text-stone-900 underline" href={`mailto:${email}`}>{email}</a>} />
        <Row label="Téléphone" value={s.legal_phone} />
        <Row label="Numéro IDE / Registre du commerce" value={s.legal_ide} />
        {!hasIdentity && (
          <p className="text-amber-700 text-sm">Les informations de l'éditeur seront complétées prochainement. Pour toute question : <a className="underline" href={`mailto:${email}`}>{email}</a>.</p>
        )}
      </div>
      <h2 className="font-serif text-xl text-stone-900 pt-4">Hébergement</h2>
      <p>L'application est hébergée sur une infrastructure cloud sécurisée. Les paiements en ligne sont traités par Stripe (cartes bancaires et TWINT) ; aucune donnée de carte n'est stockée par Notre Oui.</p>
      <h2 className="font-serif text-xl text-stone-900 pt-4">Propriété intellectuelle</h2>
      <p>L'ensemble des contenus (marque, logo, textes, interfaces) est protégé. Toute reproduction sans autorisation est interdite. Les contenus créés par les utilisateurs (faire-part, listes, textes) restent leur propriété.</p>
      <h2 className="font-serif text-xl text-stone-900 pt-4">Contact</h2>
      <p>Pour toute demande : <a className="underline" href={`mailto:${email}`}>{email}</a>.</p>
    </Shell>
  );
}

export function CGV() {
  const s = useLegal();
  const email = s.legal_email || "info@notreoui.ch";
  const name = s.legal_name || "Notre Oui";
  return (
    <Shell title="Conditions générales de vente">
      <p>Les présentes conditions générales de vente (CGV) régissent l'utilisation des offres payantes de Notre Oui, éditées par {name}.</p>
      <h2 className="font-serif text-xl text-stone-900 pt-4">1. Offres et forfaits</h2>
      <p>Notre Oui propose des forfaits Premium donnant accès à des fonctionnalités avancées pour une durée déterminée. Les forfaits sont des <strong>paiements uniques à durée fixe</strong> (par ex. mensuel = 30 jours, annuel = 365 jours), <strong>sans renouvellement automatique</strong>. À l'échéance, le compte repasse en formule gratuite sans perte de données ; l'accès Premium peut être renouvelé par un nouveau paiement.</p>
      <h2 className="font-serif text-xl text-stone-900 pt-4">2. Prix et paiement</h2>
      <p>Les prix sont indiqués en francs suisses (CHF). Le paiement s'effectue en ligne de manière sécurisée via Stripe, par carte bancaire (Visa, Mastercard) ou TWINT. L'accès Premium est activé uniquement après confirmation effective du paiement.</p>
      <h2 className="font-serif text-xl text-stone-900 pt-4">3. Droit de rétractation</h2>
      <p>S'agissant d'un service numérique à exécution immédiate, l'accès aux fonctionnalités débute dès la confirmation du paiement. Pour toute demande relative à un paiement, contactez-nous à <a className="underline" href={`mailto:${email}`}>{email}</a>.</p>
      <h2 className="font-serif text-xl text-stone-900 pt-4">4. Responsabilité</h2>
      <p>Notre Oui met tout en œuvre pour assurer la disponibilité du service mais ne saurait être tenu responsable d'interruptions indépendantes de sa volonté. Les données sont sauvegardées et ne sont pas supprimées à l'expiration d'un forfait.</p>
      <h2 className="font-serif text-xl text-stone-900 pt-4">5. Droit applicable</h2>
      <p>Les présentes CGV sont soumises au droit suisse. Pour toute question : <a className="underline" href={`mailto:${email}`}>{email}</a>.</p>
    </Shell>
  );
}

export function Confidentialite() {
  const s = useLegal();
  const email = s.legal_email || "info@notreoui.ch";
  return (
    <Shell title="Politique de confidentialité">
      <p>Notre Oui accorde une grande importance à la protection de vos données personnelles, conformément à la Loi fédérale sur la protection des données (LPD) et, le cas échéant, au RGPD.</p>
      <h2 className="font-serif text-xl text-stone-900 pt-4">Données collectées</h2>
      <p>Nous collectons les données que vous nous fournissez : identité, email, informations relatives à votre mariage (invités, budget, prestataires) et contenus créés. Ces données sont nécessaires au fonctionnement du service.</p>
      <h2 className="font-serif text-xl text-stone-900 pt-4">Finalités</h2>
      <p>Vos données servent à fournir et améliorer le service, gérer votre compte, traiter les paiements et vous adresser les communications liées à votre mariage.</p>
      <h2 className="font-serif text-xl text-stone-900 pt-4">Paiements</h2>
      <p>Les paiements sont traités par Stripe. Les données de carte ne sont jamais stockées sur nos serveurs.</p>
      <h2 className="font-serif text-xl text-stone-900 pt-4">Partage</h2>
      <p>Vos données ne sont jamais vendues. Elles peuvent être partagées avec les personnes que vous invitez à co-organiser votre mariage, et avec nos prestataires techniques strictement nécessaires (hébergement, envoi d'emails, paiement).</p>
      <h2 className="font-serif text-xl text-stone-900 pt-4">Vos droits</h2>
      <p>Vous disposez d'un droit d'accès, de rectification et de suppression de vos données. Pour exercer ces droits : <a className="underline" href={`mailto:${email}`}>{email}</a>.</p>
      <h2 className="font-serif text-xl text-stone-900 pt-4">Conservation</h2>
      <p>Vos données sont conservées tant que votre compte est actif. Vous pouvez demander leur suppression à tout moment.</p>
    </Shell>
  );
}
